import getFocusable from './get-focusable';
import isFunction from './is-function';
import keyCodes from './key-codes';

const EVENT_KEY = '_focusTrap_';
const EVENT_KEYDOWN = `keydown.${EVENT_KEY}`;
const EVENT_FOCUS = `focus.${EVENT_KEY}`;
const EVENT_BLUR = `blur.${EVENT_KEY}`;

/** 
 * @callback OnBreakout
 * @param {FocusTrap} focusTrap origin FocusTrap instance
 * @param {Object<string, any>} event details about event that triggered the breakout
 * 
 * @typedef {Object} FocusTrapElementOptions // will be added to FocusTrapOptions
 * @property {jQuery|Node|null} initialFocusElement
 * @property {jQuery|Node|null} originElement 
 * 
 * @typedef {Object} FocusTrapOptions
 * @property {boolean} allowBreakout allow tab to break the focus trap on first and last items
 * @property {boolean} initialFocus whether or not to focus on an item within trap when created
 * @property {jQuery|Node|null} initialFocusElement element to recieve focus once the trap is created - needs to be among focusable elements
 * @property {number} initialFocusIndex index of focuseable element that recieves focus once the trap is created
 * @property {jQuery|Node|null} originElement element that created the focus trap - by default focus will return to this element when trap is destroyed
 * @property {boolean} preventFocusOrigin prevent the trap from focusing on origin element when destroyed
 * @property {Array<number>} firstKeys array of key codes that jump focus to first element
 * @property {Array<number>} lastKeys array of key codes that jump focus to last element
 * @property {Array<number>} nextKeys array of key codes that jump focus to next element
 * @property {Array<number>} nextShiftKeys array of key codes that jump focus to previous element
 * @property {Array<number>} prevKeys array of key codes that jump focus to previous element
 * @property {Array<number>} prevShiftKeys array of shift + key codes that jump focus to previous element
 * @property {OnBreakout} onBreakout // callback function when user breaks out of focus trap
 */

/**
 * this will be converted to jquery objects so we're storing default values in this object for a later reference
 * - is merged into DEFAULT_OPTIONS
 * - values are converted in constructor
 * @type {FocusTrapElementOptions}
 */
const DEFAULT_ELEMENT_OPTIONS = {
    originElement: null,
    initialFocusElement: null
};

/** @type {FocusTrapOptions} */
const DEFAULT_OPTIONS = {
    allowBreakout: false,
    initialFocus: true,
    initialFocusIndex: 0,
    preventFocusOrigin: false,
    firstKeys: [ keyCodes.HOME ],
    lastKeys: [ keyCodes.END ],
    nextKeys: [ keyCodes.TAB ],
    nextShiftKeys: [],
    prevKeys: [],
    prevShiftKeys: [ keyCodes.TAB ],
    onBreakout: null,

    ...DEFAULT_ELEMENT_OPTIONS
};

export default class FocusTrap {
    /** @private */
    #activeIndex = 0;

    /**
     * 
     * @constructor
     * @param {$} $el root element of focus trap
     * @param {FocusTrapOptions} options focus trap options
     */
    constructor($el, options) {
        this.$el = $el;
        this.$focusable = getFocusable(this.$el);

        if(!this.$focusable.length) {
            return;
        }

        options = {
            ...DEFAULT_OPTIONS,
            ...{
                ...options,
                originElement: options.originElement || document.activeElement,
                nextKeys: [ ...DEFAULT_OPTIONS.nextKeys, ...(options.nextKeys || []) ],
                prevShiftKeys: [ ...DEFAULT_OPTIONS.prevShiftKeys, ...(options.prevShiftKeys || []) ]
            }
        } || DEFAULT_OPTIONS;

        // convert all element options into jQuery objects for consistency
        Object.keys(DEFAULT_ELEMENT_OPTIONS).forEach(key => {
            const el = options[key];
            if(el && !(el instanceof jQuery) && el.nodeType && el.nodeType === Node.ELEMENT_NODE) {
                options[key] = $(el);
            }
        });

        Object.assign(this, options);

        /**
         * array of all key codes that enforce focus trap
         * @type {Array<number>}
         */
        this.trapKeys = [
            ...this.firstKeys,
            ...this.lastKeys,
            ...this.nextKeys,
            ...this.prevKeys
        ];

        /**
         * array of all shift + key codes that enforce focus trap
         * @type {Array<number>}
         */
        this.trapShiftKeys = [
            ...this.nextShiftKeys,
            ...this.prevShiftKeys
        ];

        this.setInitialFocus(options);
        this.init();
    }

    get activeIndex() {
        return this.#activeIndex;
    }

    set activeIndex(val) {
        let newVal = val;

        if(val > this.maxIndex) {
            newVal = 0;
        } else if(val < 0) {
            newVal = this.maxIndex;
        }

        this.#activeIndex = newVal;
        this.$focusable.get(this.#activeIndex).focus();
    }

    get maxIndex() {
        return this.$focusable.length - 1;
    }

    get $current() {
        return this.$focusable.eq(this.#activeIndex);
    }

    get $first() {
        return this.$focusable.first();
    }

    get $last() {
        return this.$focusable.last();
    }

    get isFirst() {
        return this.#activeIndex === 0;
    }

    get isLast() {
        return this.#activeIndex === this.maxIndex;
    }

    /**
     * remove all event listeners and return focus to an origin element
     * @returns {null} return null value indicating the instance is no longer functional
     */
    destroy() {
        this.$focusable.off(EVENT_KEYDOWN);
        this.$focusable.off(EVENT_BLUR);

        if(!this.preventFocusOrigin) {
            this.originElement?.eq(0).focus();
        }

        return null;
    }

    first() {
        this.activeIndex = 0;
    }

    last() {
        this.activeIndex = this.maxIndex;
    }

    next() {
        this.activeIndex++;
    }

    prev() {
        this.activeIndex--;
    }

    /**
     * update activeIndex when a $focusable item is triggered externally
     * - update the private #activeIndex property to prevent infinite setter loop
     * - e.g. user clicks on an item instead of using keyboard controls
     * @param {number} index index of $focusable item triggering this handler
     */
    onFocus(index) {
        if(this.activeIndex !== index) {
            this.#activeIndex = index;
        }
    }

    /**
     * handle keyboard controls
     * @param {Object<string,any>} event
     */
    onKeydown(evt) {
        const key = evt.which ? evt.which : evt.keyCode;

        const isDefault = evt.ctrlKey || (evt.shiftKey && !this.trapShiftKeys.includes(key)) || (!evt.shiftKey && !this.trapKeys.includes(key));
        if(isDefault) return true;

        if(this.allowBreakout) {
            if((this.isFirst && evt.shiftKey && key === keyCodes.TAB) || (this.isLast && !evt.shiftKey && key === keyCodes.TAB)) {
                this.triggerBreakout(evt);
                return true;
            }
        }

        switch(true) {
            case (evt.shiftKey && this.nextShiftKeys.includes(key)) || (!evt.shiftKey && this.nextKeys.includes(key)):
                this.next();
                break;
            case (evt.shiftKey && this.prevShiftKeys.includes(key)) || (!evt.shiftKey && this.prevKeys.includes(key)):
                this.prev();
                break;
            case this.firstKeys.includes(key):
                this.first();
                break;
            case this.lastKeys.includes(key):
                this.last();
                break;
        }

        return false;
    }

    init() {
        this.setupListeners();
    }

    /**
     * logic dictating which element recieves focus on initialization
     * @param {FocusTrapOptions} options
     */
    setInitialFocus() {
        if(this.initialFocus) {
            let index = DEFAULT_OPTIONS.initialFocusIndex;

            if(this.initialFocusElement) {
                const matchIndex = this.$focusable.index(this.initialFocusElement);
                if(matchIndex > 0) {
                    index = matchIndex;
                }
            } else if(this.initialFocusIndex) {
                index = this.initialFocusIndex > this.maxIndex ? this.maxIndex : this.initialFocusIndex;
            }

            this.activeIndex = index;
        }
    }

    setupListeners() {
        this.$focusable.on(EVENT_KEYDOWN, evt => this.onKeydown(evt));
        this.$focusable.each((index) => {
            this.$focusable.eq(index).on(EVENT_FOCUS, () => this.onFocus(index));
        });
    }

    triggerBreakout(evt) {
        if(isFunction(this.onBreakout)) {
            this.originElement = null; // we want focus to move to next element in DOM while breaking out instead of origin element
            const $activeEl = this.$focusable.eq(this.activeIndex);
            $activeEl.one(EVENT_BLUR, () => this.onBreakout(evt, this));
        }
    }
}
